import { useContractRead } from 'wagmi';
import { BitstraysTokenABI } from '@bitstrays/contracts';
import config from '../config';

export enum BitstrayContractFunction {
  publicMint = 'publicMint',
}

interface BitstrayToken {
  name: string;
  attributes: BitstrayTrait[]
  description: string;
  image: string;
}

interface BitstrayTrait {
  trait_type: string;
  value: string;
}

export interface IBitstraySeed {
  background: number;
  arms: number;
  shirt: number;
  motive: number;
  head: number;
  eyes: number;
  mouth: number;
}

// Transform wagmi's array/object response into IBitstraySeed format
export const parseSeed = (seed: unknown): IBitstraySeed | undefined => {
  if (!seed) return undefined;

  // Wagmi may return as array or object depending on ABI
  if (Array.isArray(seed)) {
    return {
      background: Number(seed[0]),
      arms: Number(seed[1]),
      shirt: Number(seed[2]),
      motive: Number(seed[3]),
      head: Number(seed[4]),
      eyes: Number(seed[5]),
      mouth: Number(seed[6]),
    };
  }

  // If it's already an object with the right properties
  const s = seed as Record<string, unknown>;
  if ('background' in s) {
    return {
      background: Number(s.background),
      arms: Number(s.arms),
      shirt: Number(s.shirt),
      motive: Number(s.motive),
      head: Number(s.head),
      eyes: Number(s.eyes),
      mouth: Number(s.mouth),
    };
  }

  return undefined;
};

const contractConfig = {
  address: config.addresses.bitstraysToken as `0x${string}`,
  abi: BitstraysTokenABI,
};

export const useBitstrayToken = (bitstrayId: bigint) => {
  const { data: bitstray } = useContractRead({
    ...contractConfig,
    functionName: 'dataURI',
    args: [bitstrayId],
    enabled: !!bitstrayId,
  });

  if (!bitstray) {
    return;
  }

  const bitstrayImgData = (bitstray as string).split(';base64,').pop() as string;
  const json: BitstrayToken = JSON.parse(atob(bitstrayImgData));

  return json;
};

export const useBitstrayTotalSupply = (): bigint | undefined => {
  const { data: totalSupply } = useContractRead({
    ...contractConfig,
    functionName: 'totalSupply',
  });
  return totalSupply !== undefined ? BigInt(totalSupply as any) : undefined;
};

export const useBitstrayPrice = (): bigint => {
  const { data: bitstrayPrice } = useContractRead({
    ...contractConfig,
    functionName: 'bitstrayPrice',
  });
  return bitstrayPrice !== undefined ? BigInt(bitstrayPrice as any) : 0n;
};

export const useBitstraySeed = (bitstrayId: bigint) => {
  const { data: seed } = useContractRead({
    ...contractConfig,
    functionName: 'seeds',
    args: [bitstrayId],
    enabled: !!bitstrayId,
  });
  return parseSeed(seed);
};

